import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { EMPTY, throwError } from 'rxjs';
import { tap, take, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { UserInfoService } from '../services/user-info/user-info.service';
import { AccessDeniedService } from '../services/access-denied/access-denied.service';
import { getUserIdentity, getBusinessUnit } from '../auth/auth-identity';
import { environment } from '../../../environments/environment';
import { SKIP_AUTH } from '@app/constants/constant';

const ACCESS_DENIED_MSG = 'User does not has access to this location';
let isRedirectingToDashboard = false;

function parseBody(raw: unknown): Record<string, unknown> | null {
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Record<string, unknown>; } catch { /* ignore */ }
  }
  return null;
}

function isAccessDenied(raw: unknown): boolean {
  const body = parseBody(raw);
  return body?.['success'] === false && body?.['message'] === ACCESS_DENIED_MSG;
}

function isLoginRequest(url: string, body: unknown): boolean {
  return (
    url === environment.graphqlURI &&
    typeof body === 'object' &&
    body !== null &&
    'query' in body &&
    typeof (body as { query: unknown }).query === 'string' &&
    (body as { query: string }).query.includes('loginWithEmail')
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const skipAuth = req.context.get(SKIP_AUTH);
  if (skipAuth) {
    return next(req);
  }

  if (isLoginRequest(req.url, req.body)) {
    return next(req);
  }

  function isHrmsRequest(url: string): boolean {
    return url.startsWith(environment.graphqlURI) || url.startsWith(environment.restURI);
  }

  const authService = inject(AuthService);
  const userInfoService = inject(UserInfoService);
  const accessDeniedService = inject(AccessDeniedService);

  const buildRequest = () => {
    // Identity from SSO storage, with a dev-login fallback — see auth-identity.ts.
    // const detailObj = getUserIdentity();
    const detailObj = { "email": "javed.waqas2@gmail.com", "sub": "374643952432728541", "name": "Waqas Javed", "given_name": "Waqas", "family_name": "Javed", "business_unit": "MS-US" };
    const bu = getBusinessUnit();

    const token = authService.getToken();

    return req.clone({
      setHeaders: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        userdetail: JSON.stringify(detailObj),
        'ngrok-skip-browser-warning': 'true',
        ...(bu ? { 'business_unit': bu } : {}),
      },
    });
  };

  const redirectIfDenied = (raw: unknown): void => {
    if (!isAccessDenied(raw) || isRedirectingToDashboard) return;
    isRedirectingToDashboard = true;
    accessDeniedService.trigger();
    setTimeout(() => { isRedirectingToDashboard = false; }, 3000);
  };

  const sendRequest = () =>
    next(buildRequest()).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          redirectIfDenied(event.body);
        }
      }),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          if (isAccessDenied(err.error)) {
            redirectIfDenied(err.error);
            return EMPTY;
          }
          console.error('[Interceptor] HTTP error:', err.status, req.url, err.error);
        }
        return throwError(() => err);
      }),
    );

  if (isHrmsRequest(req.url) && !userInfoService.userInfo()) {
    return userInfoService.ready$.pipe(
      take(1),
      switchMap(() => sendRequest()),
    );
  }

  return sendRequest();
};
